import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Loader2, Download, ExternalLink, Globe, User as UserIcon, FileText, Link2, Mail, Wifi, Phone, MessageSquare, Contact } from "lucide-react";
import { toast } from "sonner";

const TYPE_ICON = {
  text: FileText, url: Link2, email: Mail, wifi: Wifi, vcard: Contact, phone: Phone, sms: MessageSquare,
};

function timeAgo(iso) {
  try {
    const d = new Date(iso);
    const s = Math.max(1, Math.floor((Date.now() - d.getTime()) / 1000));
    if (s < 60) return `vor ${s}s`;
    const m = Math.floor(s / 60);
    if (m < 60) return `vor ${m}m`;
    const h = Math.floor(m / 60);
    if (h < 24) return `vor ${h}h`;
    const dd = Math.floor(h / 24);
    return `vor ${dd}d`;
  } catch (_) { return iso; }
}

export default function History() {
  const [tab, setTab] = useState("frontend");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async (source) => {
    setLoading(true);
    try {
      const res = await api.get("/qr/history", { params: { source, limit: 200 } });
      setItems(res.data.items || []);
    } catch (e) {
      toast.error("Verlauf konnte nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(tab); }, [tab]);

  return (
    <Layout>
      <div className="mx-auto max-w-7xl px-4 md:px-8 py-8 md:py-12" data-testid="history-page">
        <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500 font-bold">Verlauf</div>
            <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight mt-1">Alle erzeugten QR-Codes</h1>
            <p className="mt-2 text-sm text-zinc-600 max-w-xl">
              Sichtbar für alle angemeldeten Mitarbeitenden. Frontend-Erstellungen zeigen den Ersteller, API-Erstellungen die IP-Adresse.
            </p>
          </div>
          <Button variant="outline" onClick={() => load(tab)} data-testid="refresh-history-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Aktualisieren
          </Button>
        </div>

        <Tabs value={tab} onValueChange={setTab} data-testid="history-tabs">
          <TabsList className="bg-zinc-100">
            <TabsTrigger value="frontend" data-testid="tab-frontend">
              <UserIcon className="w-4 h-4 mr-2" /> Frontend
            </TabsTrigger>
            <TabsTrigger value="api" data-testid="tab-api">
              <Globe className="w-4 h-4 mr-2" /> Public API
            </TabsTrigger>
          </TabsList>

          <TabsContent value="frontend" className="mt-6">
            <Grid items={items} loading={loading} source="frontend" />
          </TabsContent>
          <TabsContent value="api" className="mt-6">
            <Grid items={items} loading={loading} source="api" />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}

function Grid({ items, loading, source }) {
  if (loading) {
    return (
      <div className="border border-zinc-200 bg-white rounded-md p-12 grid place-items-center text-zinc-500">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }
  if (!items.length) {
    return (
      <div className="border border-dashed border-zinc-300 rounded-md p-12 text-center">
        <div className="font-display text-lg font-semibold">Keine Einträge</div>
        <div className="text-sm text-zinc-500 mt-1">
          {source === "frontend" ? "Erstelle einen QR-Code im Generator." : "API-Aufrufe werden hier angezeigt."}
        </div>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid={`history-grid-${source}`}>
      {items.map((it) => (
        <Card key={it.id} item={it} />
      ))}
    </div>
  );
}

function Card({ item }) {
  const Icon = TYPE_ICON[item.type] || FileText;
  const fg = item.options?.fg_color || "#000000";
  const bg = item.options?.bg_color || "#FFFFFF";
  const apiBase = process.env.REACT_APP_BACKEND_URL + "/api";

  const downloadFor = async (format) => {
    try {
      const res = await fetch(`${apiBase}/qr/${item.id}.${format}`, { credentials: "include" });
      if (!res.ok) throw new Error("download fail");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `qr-${item.id}.${format}`; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (_) {
      toast.error("Download fehlgeschlagen");
    }
  };

  return (
    <div className="border border-zinc-200 bg-white rounded-md p-4 flex flex-col gap-3" data-testid={`qr-card-${item.id}`}>
      <div
        className="aspect-square rounded-md overflow-hidden border border-zinc-100 grid place-items-center"
        style={{ background: bg }}
      >
        <img
          src={`${apiBase}/qr/${item.id}.svg`}
          alt={item.type}
          className="w-full h-full object-contain p-2"
          loading="lazy"
        />
      </div>
      <div className="space-y-1.5">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-zinc-900 text-white text-[10px] uppercase tracking-wider">
            <Icon className="w-3 h-3" /> {item.type}
          </span>
          <span className="text-[10px] uppercase tracking-wider text-zinc-500">{timeAgo(item.created_at)}</span>
        </div>
        <div className="text-xs text-zinc-700 font-mono break-all line-clamp-2 min-h-[2.4em]">
          {item.payload}
        </div>
        <div className="text-[11px] text-zinc-500 truncate">
          {item.source === "frontend" ? (
            <span className="inline-flex items-center gap-1"><UserIcon className="w-3 h-3" /> {item.creator_email || "?"}</span>
          ) : (
            <span className="inline-flex items-center gap-1"><Globe className="w-3 h-3" /> IP {item.creator_ip || "?"}</span>
          )}
        </div>
      </div>
      <div className="flex gap-2 mt-auto">
        <Button size="sm" variant="outline" className="flex-1" onClick={() => downloadFor("png")} data-testid={`card-download-png-${item.id}`}>
          <Download className="w-3.5 h-3.5 mr-1" /> PNG
        </Button>
        <Button size="sm" variant="outline" className="flex-1" onClick={() => downloadFor("svg")} data-testid={`card-download-svg-${item.id}`}>
          <Download className="w-3.5 h-3.5 mr-1" /> SVG
        </Button>
      </div>
    </div>
  );
}
